import React, { useEffect, useState } from 'react';

import type { MatchQuery } from '@confluence/route/entry-points/matchUrl.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { EDIT_BLOG_V2, EDIT_PAGE_V2, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { BadStatusError, cfetch } from '@confluence/network';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

// RedirectOldCreatePage uses this placeholder when a draft link has no space key.
const BY_DRAFT_SPACE_KEY = 'byDraft';
const REDIRECT_SOURCE = 'legacyCreateRouteRedirect';

type LegacyCreateContentType = 'page' | 'blog';

type DraftToOpen = {
	draftId: string;
	draftShareId: string | undefined;
	/** The space key from the URL, or undefined for byDraft links. */
	spaceKey: string | undefined;
	contentType: LegacyCreateContentType;
};

type LegacyCreateTarget = { href: string } | { draft: DraftToOpen };

/**
 * Maps a legacy (TinyMCE) create route URL - /spaces/:spaceKey/pages/create or
 * /spaces/:spaceKey/blog/create - to its Fabric equivalent. A new page or blog post goes to the
 * create-content flow, and a draft has to be looked up first (see resolveDraftEditUrl). None of
 * the targets lead back to a legacy create route, so the redirect cannot loop.
 */
export const getLegacyCreateTarget = ({
	contentType,
	spaceKey,
	query,
}: {
	contentType: LegacyCreateContentType;
	spaceKey: string | undefined;
	query: MatchQuery | undefined;
}): LegacyCreateTarget => {
	const rawDraftId = getSingleParam(query, 'draftId');
	const draftId = rawDraftId && /^[0-9]+$/.test(rawDraftId) ? rawDraftId : undefined;
	const isByDraft = !spaceKey || spaceKey === BY_DRAFT_SPACE_KEY;

	if (draftId) {
		return {
			draft: {
				draftId,
				draftShareId: getSingleParam(query, 'draftShareId'),
				spaceKey: isByDraft ? undefined : spaceKey,
				contentType,
			},
		};
	}

	if (isByDraft) {
		// A byDraft link without a usable draft id has nothing to open or create.
		return { href: WIKI.toUrl() };
	}

	if (contentType === 'blog') {
		return { href: buildCreationLink({ contentType: 'blog', spaceKey, source: REDIRECT_SOURCE }) };
	}

	return {
		href: buildCreationLink({
			contentType: 'page',
			spaceKey,
			parentPageId: getSingleParam(query, 'parentPageId') || getSingleParam(query, 'fromPageId'),
			title: getSingleParam(query, 'title'),
			source: REDIRECT_SOURCE,
		}),
	};
};

const getDraftEditUrl = ({
	draftId,
	draftShareId,
	spaceKey,
	isBlog,
}: {
	draftId: string;
	draftShareId: string | undefined;
	spaceKey: string;
	isBlog: boolean;
}): string =>
	(isBlog ? EDIT_BLOG_V2 : EDIT_PAGE_V2).toUrl(
		{ contentType: isBlog ? 'blog' : 'pages', spaceKey, contentId: draftId },
		{ query: draftShareId ? { draftShareId } : {} },
	);

/**
 * The legacy create page looked a draft up before opening it, and so does this: the URL's space
 * key can be the byDraft placeholder (or stale), and its content type is "pages" even for blog
 * drafts. The draft id is the content id, so edit-v2 opens the draft of a published page too.
 */
export const resolveDraftEditUrl = async ({
	draftId,
	draftShareId,
	spaceKey,
	contentType,
}: DraftToOpen): Promise<string> => {
	try {
		const response = await cfetch(
			`${CONTEXT_PATH}/rest/api/content/${draftId}?status=draft&expand=space`,
		);
		const draft = await response.json();
		const draftSpaceKey: string | undefined = draft?.space?.key;
		if (draftSpaceKey) {
			return getDraftEditUrl({
				draftId,
				draftShareId,
				spaceKey: draftSpaceKey,
				isBlog: draft.type === 'blogpost',
			});
		}
	} catch (error) {
		// Not found or no permission is expected for stale links; report anything else.
		if (!(error instanceof BadStatusError)) {
			getMonitoringClient().submitError(error, { attribution: Attribution.EDITOR_CORE });
		}
	}

	// Without the lookup, fall back to what the URL says. edit-v2 shows its own error if the draft
	// cannot be opened (a draftShareId may also grant access the lookup did not have).
	return spaceKey
		? getDraftEditUrl({ draftId, draftShareId, spaceKey, isBlog: contentType === 'blog' })
		: WIKI.toUrl();
};

const LegacyCreateDraftRedirect = ({
	draftId,
	draftShareId,
	spaceKey,
	contentType,
}: DraftToOpen): React.JSX.Element | null => {
	const [href, setHref] = useState<string | null>(null);

	useEffect(() => {
		let isCancelled = false;
		void resolveDraftEditUrl({ draftId, draftShareId, spaceKey, contentType }).then((url) => {
			if (!isCancelled) {
				setHref(url);
			}
		});
		return () => {
			isCancelled = true;
		};
	}, [draftId, draftShareId, spaceKey, contentType]);

	return href ? <Redirection href={href} reason={REDIRECT_SOURCE} /> : null;
};

export const LegacyCreateRouteRedirect = ({
	contentType,
	spaceKey,
	query,
}: {
	contentType: LegacyCreateContentType;
	spaceKey: string | undefined;
	query: MatchQuery | undefined;
}): React.JSX.Element | null => {
	const target = getLegacyCreateTarget({ contentType, spaceKey, query });

	return 'draft' in target ? (
		<LegacyCreateDraftRedirect {...target.draft} />
	) : (
		<Redirection href={target.href} reason={REDIRECT_SOURCE} />
	);
};
